import React, { useState, useEffect, useRef, useCallback } from "react";
import "./style/frontBackPhoto.scss";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import Webcam from "react-webcam";
import { ClipLoader } from "react-spinners";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import { clearAuth } from "../../store/slices/authSlice";
import { touchlessService } from "../../services/api/touchlessService";
import { deleteAllCookies, ID_STATUS_ENUM } from "../../utils/helpers";
import ConfirmationModal from "../../components/common/ConfirmationModal/ConfirmationModal";
import { showError } from "../../services/toastservice/toastService";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useCamera } from "../../hooks/useCamera";
import { BackButton } from "../../components/ui/Button/backButton";
import { useStrings } from "../../hooks/useStrings";
import { usePassportScanSession, MAX_SCAN_ATTEMPTS } from "../../hooks/usePassportScanSession";
import { useFrontAutoCapture } from "../../hooks/useFrontAutoCapture";
import { preloadMrzOcr, terminateMrzOcr } from "../../services/ocr/mrzOcrWorker";
import { buildMrzExtractionFields } from "../../utils/mrz/passportExtraction";
import { useBarcodeScanSession } from "../../hooks/useBarcodeScanSession";
import { preloadPdf417Reader, terminatePdf417Reader } from "../../services/scan/pdf417Reader";
import { buildBarcodeExtractionFields } from "../../utils/aamva/barcodeExtraction";
import {
  BARCODE_VIDEO_CONSTRAINTS,
  setTorch,
  tuneBackCamera,
  videoTrackOf,
  type CameraTuning,
} from "../../services/scan/cameraTuning";

// EN-4743. Photos sent to validateId for a licence / state ID: long edge <= 2000 px
// at JPEG 0.9 (the kiosk sends 2000 px at 0.8).
const UPLOAD_MAX_DIMENSION = 2000;
// EN-4743. How long a camera may stay open without its countdown / scan starting
// before Capture is offered instead (the stream never reported live).
const CAMERA_LIVE_TIMEOUT_MS = 3000;
const UPLOAD_JPEG_QUALITY = 0.9;
const uploadDimensions = (video: HTMLVideoElement | null | undefined) => {
  const w = video?.videoWidth ?? 0;
  const h = video?.videoHeight ?? 0;
  if (!w || !h) return undefined;
  const scale = Math.min(1, UPLOAD_MAX_DIMENSION / Math.max(w, h));
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
};
// The current video frame at upload size, drawn at once (a few ms). Null when it
// can't be drawn; the caller then uses react-webcam's getScreenshot as before.
const drawUploadFrame = (video: HTMLVideoElement | null | undefined): HTMLCanvasElement | null => {
  const dims = uploadDimensions(video);
  if (!video || !dims) return null;
  const canvas = document.createElement("canvas");
  canvas.width = dims.width;
  canvas.height = dims.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    ctx.drawImage(video, 0, 0, dims.width, dims.height);
  } catch {
    return null;
  }
  return canvas;
};
// JPEG data URL, encoded asynchronously where the browser can (toBlob), else the
// synchronous toDataURL. Null if encoding failed.
const encodeJpeg = (canvas: HTMLCanvasElement, quality: number): Promise<string | null> =>
  new Promise((resolve) => {
    const syncFallback = () => {
      try {
        resolve(canvas.toDataURL("image/jpeg", quality));
      } catch {
        resolve(null);
      }
    };
    if (typeof canvas.toBlob !== "function") {
      syncFallback();
      return;
    }
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          syncFallback();
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
        reader.onerror = syncFallback;
        reader.readAsDataURL(blob);
      },
      "image/jpeg",
      quality,
    );
  });
import type { ValidateIdPayload } from "../../types/idValidation";
import { middlewareService } from "../../services/middleware/middlewareService";
import { shouldShowVideo } from "../../utils/videoStep";
import { isIdValidationEnabled } from "../../utils/idCapture";
import { useEnhancedIdChecks } from "../../hooks/useEnhancedIdChecks";


interface IdValidationResponse {
  identityStatus: string;
  identityId: string;
  offenderCheckId: string;
  offenderStatus: string;
  message?: string;
  [key: string]: unknown;
}

const FrontBackPicture: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { themeColors } = useAppConfig();
  const { permissionState, requestPermission } = useCamera();
  const {
    idValidationFields,
    visitorTypeId,
    visitorTypeInfo: visitorTypeField,
    visitorinfo,
    videoUrl,
    videoSkip,
    updateVisitorInfo,
    updateIdAndOffenderValidatedModels,
  } = useVisitorData();

  const { startEnhancedIdChecks } = useEnhancedIdChecks();

  const photoFlag = Boolean(visitorTypeField?.photoFlag);

  // Validation tracking state - initialized from session storage
  const [validateAttempt, setValidateAttempt] = useState<number>(
    () => idValidationFields.idValidatedModels.length,
  );
  // Non-lazy state (dynamic values that change during component lifecycle)
  const [backIDPicture, setBackIDPicture] = useState<string>("");
  const [frontIDPicture, setFrontIDPicture] = useState<string>("");
  const [, setMultipleWebcamsAvailable] = useState<boolean>(false);
  const [facingMode] = useState<string>("environment");
  const [idValidationResponseObj, setIdValidationResponseObj] = useState<
    IdValidationResponse | Record<string, never>
  >({});
  const [cameraError, setCameraError] = useState<"busy" | null>(null);
  const [frontWebcamKey, setFrontWebcamKey] = useState(0);
  const [backWebcamKey, setBackWebcamKey] = useState(0);

  // Internal step navigation: "front" → "back"
  const [captureStep, setCaptureStep] = useState<"front" | "back">("front");

  // Modal states
  const [showFrontModal, setShowFrontModal] = useState<boolean>(false);
  const [showBackModal, setShowBackModal] = useState<boolean>(false);
  const [showValidationModal, setShowValidationModal] =
    useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  // Webcam refs
  const frontWebcamRef = useRef<Webcam>(null);
  const backWebcamRef = useRef<Webcam>(null);

  // Video constraints - request high resolution for better screenshot quality
  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1080 },
    height: { ideal: 1020 },
  };

  const frontClickRef = useRef<HTMLButtonElement>(null);
  const backsideClickRef = useRef<HTMLButtonElement>(null);
  // EN-4743. How long the last back photo took to encode, for the scan summary.
  const screenshotMsRef = useRef<number>(0);

  // EN-4743. Passport: the MRZ is read live from the camera during a countdown.
  const isPassport = idValidationFields.idCaptureType === "PASSPORT";
  // The live <video> and the passport guide over it, kept in state so the scan
  // hooks start once both exist.
  const [frontVideoEl, setFrontVideoEl] = useState<HTMLVideoElement | null>(null);
  const [passportGuideEl, setPassportGuideEl] = useState<HTMLDivElement | null>(null);
  // EN-4743. Driver's licence / state ID: the PDF417 on the back is read live
  // from the camera during a countdown. The front stays a plain photo.
  const isBarcodeId =
    idValidationFields.idCaptureType === "DRIVING_LICENCE" ||
    idValidationFields.idCaptureType === "STATE_ISSUED_ID";
  const [backVideoEl, setBackVideoEl] = useState<HTMLVideoElement | null>(null);
  const [cardGuideEl, setCardGuideEl] = useState<HTMLDivElement | null>(null);
  // The passport / barcode scanning above is only for visitor types with ID
  // validation on. Otherwise (ID capture only, or the offender check without ID
  // validation) the step is the production one for every ID type: each photo is
  // taken by the 5 -> 1 countdown and sent on Continue, with nothing scanned, and
  // Retry after an Invalid result goes back to the ID type screen.
  const idValidationEnabled = isIdValidationEnabled(visitorTypeField);
  const scanPassport = isPassport && idValidationEnabled;
  const scanBarcode = isBarcodeId && idValidationEnabled;
  const needsBackCapture =
    idValidationFields.idCaptureType !== "PASSPORT" &&
    idValidationFields.idCaptureType !== "OTHERS";
  // Set when a scan session took the photo and it should go for validation
  // without a Continue tap. Consumed once the photo is in visitorinfo (below).
  const [autoSubmitPending, setAutoSubmitPending] = useState<boolean>(false);

  useEffect(() => {
    // Get available video inputs
    navigator.mediaDevices //=====================================Do we need this?
      .enumerateDevices()
      .then((devices) => {
        const videoInputs = devices.filter(
          (device) => device.kind === "videoinput",
        );
        setMultipleWebcamsAvailable(videoInputs.length > 1);
      })
      .catch(console.error);

    // Add body class
    document.body.classList.add("front-back-capture");

    return () => {
      // ngOnDestroy equivalent
      document.body.classList.remove("front-back-capture");
    };
  }, []);

  // EN-4743. Load the OCR engine and model only for passports (≈ 5.5 MB the first
  // time, cached after); free it when the visitor leaves the page.
  useEffect(() => {
    if (!scanPassport) return;
    void preloadMrzOcr();
    return () => {
      void terminateMrzOcr();
    };
  }, [scanPassport]);

  // EN-4743. Load the barcode decoder (≈ 1 MB, cached after, running in a Web
  // Worker) only for a licence or state ID, while the visitor photographs the
  // front; free it when the visitor leaves the page.
  useEffect(() => {
    if (!scanBarcode) return;
    void preloadPdf417Reader();
    return () => {
      void terminatePdf417Reader();
    };
  }, [scanBarcode]);

  useEffect(() => {
    if (permissionState !== "granted" && permissionState !== "checking") {
      setShowFrontModal(false);
      setShowBackModal(false);
      setCameraError(null);
    }
  }, [permissionState]);

  const getIdName = (type: string): string => {
    switch (type) {
      case "DRIVING_LICENCE":
        return S.FRONT_BACK_PHOTO.ID_DRIVERS_LICENSE;
      case "STATE_ISSUED_ID":
        return S.FRONT_BACK_PHOTO.ID_STATE_ISSUED;
      case "PASSPORT":
        return S.FRONT_BACK_PHOTO.ID_PASSPORT;
      case "OTHERS":
        return S.FRONT_BACK_PHOTO.ID_OTHERS;
      default:
        return "";
    }
  };

  const onBack = () => {
    if (captureStep === "back") {
      // EN-4743. Leaving the back mid-scan: stop it, so no photo is taken of a
      // camera that's no longer on screen. Coming back restarts the same attempt.
      barcodeScan.restartAttempt();
      backAuto.stop();
      setAutoSubmitPending(false);
      setBackManualFallback(false);
      backCaptureSeqRef.current += 1;
      setCaptureStep("front");
      return;
    }
    navigate("/capturepicture");
  };

  const onFrontEnableCamera = () => {
    setFrontIDPicture("");
    setCameraError(null);
    // EN-4743. The auto-capture countdown starts again once the camera is live.
    frontAuto.stop();
    setFrontManualFallback(false);
    // EN-4743. A retake reads the passport again, in the same attempt: the 3
    // attempts are shared with Invalid results, so a retake can't add more.
    passportScan.restartAttempt();
    setAutoSubmitPending(false);
    setShowFrontModal(true);
  };

  const onBackEnableCamera = () => {
    setBackIDPicture("");
    setCameraError(null);
    // EN-4743. A retake reads the barcode again, in the same attempt.
    barcodeScan.restartAttempt();
    // ID capture only: the countdown starts again once the camera is live.
    backAuto.stop();
    setAutoSubmitPending(false);
    setBackManualFallback(false);
    backCaptureSeqRef.current += 1;
    setShowBackModal(true);
  };

  const handleFrontImage = useCallback(
    (imageSrc: string) => {
      const base64String = imageSrc.split(",")[1]; // Remove data:image/jpeg;base64, prefix
      setFrontIDPicture(imageSrc);

      const updatedVisitorInfo = {
        ...visitorinfo,
        idFrontImageBase64String: base64String,
      };

      updateVisitorInfo(updatedVisitorInfo);
    },
    [visitorinfo, updateVisitorInfo],
  );

  const handleBackImage = useCallback(
    (imageSrc: string) => {
      const base64String = imageSrc.split(",")[1]; // Remove data:image/jpeg;base64, prefix
      setBackIDPicture(imageSrc);

      const updatedVisitorInfo = {
        ...visitorinfo,
        idBackImageBase64String: base64String,
      };

      updateVisitorInfo(updatedVisitorInfo);
    },
    [visitorinfo, updateVisitorInfo],
  );

  const triggerFrontIDCapture = useCallback(() => {
    if (frontWebcamRef.current) {
      const imageSrc = frontWebcamRef.current.getScreenshot();
      if (imageSrc) {
        handleFrontImage(imageSrc);
      }
    }
    setShowFrontModal(false);
  }, [handleFrontImage]);

  // EN-4743. Passport scan session. It takes the photo itself when the countdown
  // ends (with a valid read, or after the last failed scan), through the same
  // triggerFrontIDCapture the Capture button uses.
  const requestAutoSubmit = useCallback(() => setAutoSubmitPending(true), []);
  const passportScan = usePassportScanSession({
    enabled: scanPassport,
    videoEl: frontVideoEl,
    stageEl: frontVideoEl,
    guideEl: passportGuideEl,
    takePhoto: triggerFrontIDCapture,
    autoSubmit: requestAutoSubmit,
  });

  // EN-4743. Licence / state ID / Others front (and a passport with ID capture
  // only): a 5 -> 1 countdown takes the photo, as a tap on Capture would (no
  // scanning, nothing sent on its own). Only while the front camera is actually on
  // screen: if it closed meanwhile (busy, permission, step change) the countdown
  // ending does nothing.
  const autoCaptureFront = useCallback(() => {
    if (frontWebcamRef.current) triggerFrontIDCapture();
  }, [triggerFrontIDCapture]);
  const frontAuto = useFrontAutoCapture({ enabled: !scanPassport, takePhoto: autoCaptureFront });

  // EN-4743. Front Capture button, only where it's the way forward:
  //  - licence / state ID / Others: never while the countdown takes the photo;
  //  - passport: never while scanning or after a missed scan (Retry only; the last
  //    miss sends the photo on its own), only when the OCR couldn't load or start
  //    (manual flow).
  // For both, a safety net: the front camera is open but the countdown / scan hasn't
  // started within a few seconds (the stream never reported live) → Capture.
  const [frontManualFallback, setFrontManualFallback] = useState<boolean>(false);
  const frontCameraOpen = captureStep === "front" && showFrontModal && !frontIDPicture && cameraError !== "busy";
  const frontWaitingToStart = scanPassport ? passportScan.state === "idle" : !frontAuto.running;
  const showFrontCapture = scanPassport
    ? passportScan.state === "unavailable" || (frontManualFallback && passportScan.state === "idle")
    : frontManualFallback && !frontAuto.running;
  useEffect(() => {
    if (!frontCameraOpen || !frontWaitingToStart) return;
    const timer = setTimeout(() => {
      setFrontManualFallback(true);
      middlewareService.logWarn("id_front_camera_not_live", { afterMs: CAMERA_LIVE_TIMEOUT_MS });
    }, CAMERA_LIVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [frontCameraOpen, frontWaitingToStart]);

  const onFrontCaptureClick = () => {
    frontAuto.stop();
    passportScan.onManualCapture();
    triggerFrontIDCapture();
  };

  const onFrontCameraLive = () => {
    setFrontVideoEl(frontWebcamRef.current?.video ?? null);
    passportScan.onCameraLive();
    frontAuto.onCameraLive();
  };

  // EN-4743. Bumped whenever the back capture is abandoned (Retake, leaving the
  // step, Retry), so a photo still being encoded for an old capture is dropped.
  const backCaptureSeqRef = useRef<number>(0);
  const handleBackImageRef = useRef(handleBackImage);
  useEffect(() => {
    handleBackImageRef.current = handleBackImage;
  }, [handleBackImage]);

  // Returns the photo when it's ready at once, so the barcode session can read it
  // again after the last miss (it prefers its own full-resolution still anyway).
  const triggerBackIDCapture = useCallback((): string | null => {
    // EN-4743. Licence / state ID: encode the JPEG asynchronously (canvas.toBlob),
    // off the page's main thread. The synchronous encode froze the page for ~60-100
    // ms on a laptop at this moment (more on a phone), which felt like lag. The
    // camera stays on screen until the photo is ready, so there's no flicker.
    const frame = scanBarcode ? drawUploadFrame(backWebcamRef.current?.video) : null;
    if (frame) {
      const seq = ++backCaptureSeqRef.current;
      const encodeStarted = performance.now();
      void encodeJpeg(frame, UPLOAD_JPEG_QUALITY).then((dataUrl) => {
        if (seq !== backCaptureSeqRef.current) return;
        screenshotMsRef.current = Math.round(performance.now() - encodeStarted);
        if (dataUrl) handleBackImageRef.current(dataUrl);
        setShowBackModal(false);
      });
      return null;
    }
    let imageSrc: string | null = null;
    if (backWebcamRef.current) {
      // EN-4743. The licence / state ID back streams at up to 4K for the barcode;
      // the photo sent to validateId is kept to a 2000 px long edge, as the kiosk
      // does (a full-resolution front + back made a 10.6 MB request there, over
      // the API gateway's 10 MB limit). Decoding never uses this JPEG.
      const encodeStarted = performance.now();
      imageSrc = backWebcamRef.current.getScreenshot(
        scanBarcode ? uploadDimensions(backWebcamRef.current.video) : undefined,
      );
      screenshotMsRef.current = Math.round(performance.now() - encodeStarted);
      if (imageSrc) {
        handleBackImage(imageSrc);
      }
    }
    setShowBackModal(false);
    return imageSrc;
  }, [handleBackImage, scanBarcode]);

  // EN-4743. Back camera settings in use (resolution, zoom, focus, torch), for the
  // torch button and the scan summary telemetry.
  // EN-4743. The back camera is open but the scan never started (the stream didn't
  // report live): after a few seconds, offer Capture so the visitor isn't stuck.
  const [backManualFallback, setBackManualFallback] = useState<boolean>(false);
  const [cameraTuning, setCameraTuning] = useState<CameraTuning | null>(null);
  const [torchOn, setTorchOn] = useState<boolean>(false);
  const cameraTuningRef = useRef<CameraTuning | null>(null);
  const torchOnRef = useRef<boolean>(false);
  useEffect(() => {
    cameraTuningRef.current = cameraTuning;
    torchOnRef.current = torchOn;
  }, [cameraTuning, torchOn]);
  const getCameraInfo = useCallback(
    () => ({ ...(cameraTuningRef.current ?? {}), torchOn: torchOnRef.current, screenshotMs: screenshotMsRef.current }),
    [],
  );

  // EN-4743. Licence / state ID barcode session on the back camera. Same flow as
  // the passport, plus one read of the photo itself after the last miss.
  const barcodeScan = useBarcodeScanSession({
    enabled: scanBarcode,
    videoEl: backVideoEl,
    stageEl: backVideoEl,
    guideEl: cardGuideEl,
    takePhoto: triggerBackIDCapture,
    autoSubmit: requestAutoSubmit,
    getCameraInfo,
  });

  // Licence / state ID back with ID capture only: the same 5 -> 1 countdown as the
  // front takes the photo (no barcode scan, nothing sent on its own). Only while
  // the back camera is on screen.
  const autoCaptureBack = useCallback(() => {
    if (backWebcamRef.current) triggerBackIDCapture();
  }, [triggerBackIDCapture]);
  const backAuto = useFrontAutoCapture({ enabled: needsBackCapture && !scanBarcode, takePhoto: autoCaptureBack });

  const onBackCaptureClick = () => {
    barcodeScan.onManualCapture();
    backAuto.stop();
    triggerBackIDCapture();
  };

  const onBackCameraLive = () => {
    const video = backWebcamRef.current?.video ?? null;
    setBackVideoEl(video);
    setTorchOn(false);
    // EN-4743. Autofocus and zoom where the browser allows them, so the card can
    // be held ~20 cm away. Never blocks the scan.
    if (scanBarcode) {
      void tuneBackCamera(videoTrackOf(video)).then(setCameraTuning).catch(() => setCameraTuning(null));
    }
    barcodeScan.onCameraLive();
    backAuto.onCameraLive();
  };

  const onToggleTorch = () => {
    const next = !torchOn;
    void setTorch(videoTrackOf(backWebcamRef.current?.video), next).then((ok) => {
      if (ok) setTorchOn(next);
    });
  };

  // EN-4743. Licence / state ID back: no Capture while scanning is possible, only
  // Retry after a miss (the last scan always sends on its own). Capture is the
  // way forward only when the reader can't run, or the camera opened but never
  // went live (safety net below). With ID capture only, the countdown takes the
  // photo and Capture is the same safety net as on the front.
  const backCameraOpen = captureStep === "back" && showBackModal && !backIDPicture && cameraError !== "busy";
  const backWaitingToStart = scanBarcode ? barcodeScan.state === "idle" : !backAuto.running;
  const showBackCapture = scanBarcode
    ? barcodeScan.state === "unavailable" || (backManualFallback && barcodeScan.state === "idle")
    : backManualFallback && !backAuto.running;
  useEffect(() => {
    if (!backCameraOpen || !backWaitingToStart) return;
    const timer = setTimeout(() => {
      setBackManualFallback(true);
      middlewareService.logWarn(scanBarcode ? "id_barcode_camera_not_live" : "id_back_camera_not_live", {
        afterMs: CAMERA_LIVE_TIMEOUT_MS,
      });
    }, CAMERA_LIVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [scanBarcode, backCameraOpen, backWaitingToStart]);

  const handleInitError = useCallback((error: string | DOMException) => {
    if (typeof error === "string") {
      showError(error);
      return;
    }
    if (error?.name === "NotAllowedError") {
      requestPermission();
    } else if (error?.name === "NotReadableError" || error?.name === "TrackStartError") {
      showError(S.FRONT_BACK_PHOTO.ERROR_CAMERA_IN_USE);
      setCameraError("busy");
    } else {
      showError(S.FRONT_BACK_PHOTO.ERROR_CAMERA_INIT_FAILED);
    }
  }, [requestPermission]);

  const handleTryNow = useCallback(async (side: "front" | "back") => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      setCameraError(null);
      if (side === "front") setFrontWebcamKey(k => k + 1);
      else setBackWebcamKey(k => k + 1);
    } catch {
      showError(S.FRONT_BACK_PHOTO.ERROR_CAMERA_BUSY_RETRY);
    }
  }, []);

  const handleContinue = () => {
    if (
      idValidationFields.idCaptureType === "PASSPORT" ||
      idValidationFields.idCaptureType === "OTHERS"
    ) {
      if (!frontIDPicture) {
        showError(S.FRONT_BACK_PHOTO.ERROR_PLEASE_SCAN_DOCS);
        return;
      }
    } else {
      if (!frontIDPicture || !backIDPicture) {
        showError(S.FRONT_BACK_PHOTO.ERROR_PLEASE_SCAN_DOCS);
        return;
      }
    }

    setIdValidationResponseObj({});
    handleValidateId();
  };

  const handleValidateId = async (auto = false) => {
    setIsLoading(true);
    const sendStarted = performance.now();

    // EN-4743. identityType tells the backend which document it is looking at
    // (the kiosk sends it too). For a passport read on the device, the extracted
    // details go with the photo, in the kiosk's format; with no read the request
    // is exactly today's plus identityType, and the backend reads the photo.
    // With ID capture only, the request is exactly the production one: the photos,
    // no identityType and nothing extracted.
    const payload: ValidateIdPayload = {
      visitorTypeId: visitorTypeId,
      siteId: visitorTypeField?.siteId,
      identityType: idValidationEnabled ? idValidationFields.idCaptureType || undefined : undefined,
      base64EncodedFrontImage: visitorinfo?.idFrontImageBase64String as string | undefined,
      base64EncodedBackImage: visitorinfo?.idBackImageBase64String as string | undefined,
      ...buildMrzExtractionFields(scanPassport ? passportScan.mrzData : null),
      // Licence / state ID: the back's barcode, when it was read.
      ...buildBarcodeExtractionFields(scanBarcode ? barcodeScan.barcode : null),
    };

    try {
      const response = (await touchlessService.validateId(
        payload,
      )) as IdValidationResponse;
      // EN-4743. How long validateId took (network + backend), for scanned IDs.
      if (scanPassport || scanBarcode) {
        middlewareService.logInfo("id_validate_sent", {
          identityType: idValidationFields.idCaptureType,
          auto,
          sendMs: Math.round(performance.now() - sendStarted),
        });
      }
      setIdValidationResponseObj(response);
      performActionAccordingToStatus(response);
    } catch (error) {
      console.error("ID validation error:", error);
      showError("ID validation failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // EN-4743. A photo taken by a scan session is sent without a Continue tap.
  // handleFrontImage / handleBackImage store it through Redux, so it isn't in
  // visitorinfo in the same render the photo was taken; wait for it here (the
  // front for a passport, the back for a licence / state ID), then send exactly
  // as Continue does (same pattern as the kiosk's auto-advance).
  const handleValidateIdRef = useRef(handleValidateId);
  useEffect(() => {
    handleValidateIdRef.current = handleValidateId;
  });
  const autoSubmitPhoto = scanBarcode
    ? visitorinfo?.idBackImageBase64String
    : visitorinfo?.idFrontImageBase64String;
  useEffect(() => {
    if (!autoSubmitPending || !autoSubmitPhoto || isLoading) return;
    setAutoSubmitPending(false);
    setIdValidationResponseObj({});
    void handleValidateIdRef.current(true);
  }, [autoSubmitPending, autoSubmitPhoto, isLoading]);

  const performActionAccordingToStatus = (
    responseObj: IdValidationResponse,
  ) => {
    const identityStatus = responseObj?.identityStatus?.toUpperCase();

    // Common logic for all cases
    const newIdValidatedModels = [
      ...idValidationFields.idValidatedModels,
      {
        identityId: responseObj?.identityId,
        identityStatus: responseObj?.identityStatus,
      },
    ];
    const newOffenderValidatedModels = [
      ...idValidationFields.offenderValidatedModels,
      {
        offenderCheckId: responseObj?.offenderCheckId,
        offenderStatus: responseObj?.offenderStatus,
      },
    ];

    updateIdAndOffenderValidatedModels(
      newIdValidatedModels,
      newOffenderValidatedModels,
    );
    updateVisitorInfo({
      ...visitorinfo,
      idValidatedModels: newIdValidatedModels,
      offenderValidatedModels: newOffenderValidatedModels,
    });

    setValidateAttempt((prev) => prev + 1);

    switch (identityStatus) {
      case ID_STATUS_ENUM.INVALID:
      case ID_STATUS_ENUM.SYSTEM_ERROR:
      case ID_STATUS_ENUM.ID_UNRECOGNIZED:
        // EN-4743. Passport, licence and state ID share one budget of 3
        // attempts between scan misses and non-valid results: with an attempt
        // left, offer Retry (the existing modal); on the last one, move on (the
        // result stays on the visit). validateAttempt also caps it at 3 calls
        // if the visitor left and re-entered this page. "Others" (and any ID with
        // ID capture only) keeps up to 3 validation attempts with Retry, as before.
        if (scanPassport || scanBarcode) {
          const scan = scanPassport ? passportScan : barcodeScan;
          if (scan.attemptsLeft > 0 && validateAttempt < 2) {
            setShowValidationModal(true);
          } else {
            redirectToNextPage();
          }
        } else if (validateAttempt < 2) {
          setShowValidationModal(true);
        } else {
          redirectToNextPage();
        }
        break;

      case ID_STATUS_ENUM.VALID: {
        setShowValidationModal(true);
        break;
      }

      default: {
        redirectToNextPage();
        break;
      }
    }
  };

  const redirectToNextPage = () => {
    setShowValidationModal(false);

    if (photoFlag) {
      navigate("/visitorpicture");
    } else {
      // EN-5235: no photo step, so this is the last moment before the visit is created.
      startEnhancedIdChecks();
      if (shouldShowVideo(videoUrl, videoSkip)) {
        navigate("/visitor-video");
      } else {
        navigate("/confirmation", { replace: true });
      }
    }
  };

  const retryIdValidationSteps = () => {
    setShowValidationModal(false);
    setAutoSubmitPending(false);

    // EN-4743. Licence / state ID: keep the front photo and scan only the back
    // again, in the next attempt of the shared budget.
    if (scanBarcode) {
      updateVisitorInfo({ ...visitorinfo, idBackImageBase64String: undefined });
      setBackIDPicture("");
      setBackManualFallback(false);
      backCaptureSeqRef.current += 1;
      barcodeScan.nextAttemptAfterRejection();
      setCaptureStep("back");
      return;
    }

    const clearedVisitorInfo = {
      ...visitorinfo,
      idFrontImageBase64String: undefined,
      idBackImageBase64String: undefined,
    };

    updateVisitorInfo(clearedVisitorInfo);
    setFrontIDPicture("");
    setBackIDPicture("");
    // Without ID validation (the offender check alone can return Invalid), Retry is
    // the production one: back to the ID type screen.
    if (!idValidationEnabled) {
      navigate("/capturepicture");
      return;
    }
    // EN-4743. Like the kiosk (clearCapturesForRetry): photos and on-device reads
    // are cleared and the visitor stays here to photograph the ID again, rather
    // than going back to the ID type screen. A passport moves on to its next
    // attempt; the validation attempt count (idValidatedModels) is untouched, so
    // the third failure still moves on.
    if (scanPassport) passportScan.nextAttemptAfterRejection();
    setCaptureStep("front");
  };

  const handleCancel = () => {
    dispatch(clearAuth());
    sessionStorage.clear();
    deleteAllCookies();
    setShowCancelModal(false);
    navigate("/");
  };

  return (
    <>
      <div className="main-screen" data-testid="FBP-div-main-screen">
        <div
          className="screen-outer"
          id="idcapture-types"
          data-testid="FBP-div-screen-outer"
        >
          <div
            className="screen-header-fixed"
            data-testid="FBP-div-screen-header-fixed"
          >
            <div
              className="header-fixed-left"
              data-testid="FBP-div-header-fixed-left"
            >
              <BackButton onBack={onBack} />
            </div>
            <div
              className="header-fixed-right"
              data-testid="FBP-div-header-fixed-right"
            ></div>
          </div>

          <div className="screen-inner" data-testid="FBP-div-screen-inner">
            <div
              className="screen-subinner"
              data-testid="FBP-div-screen-subinner"
            >
              <div className="screen-body" data-testid="FBP-div-screen-body">
                <h5 className="screen-title" data-testid="FBP-h5-screen-title">
                  {idValidationFields.idCaptureType
                    ? getIdName(idValidationFields.idCaptureType)
                    : ""}
                </h5>
                <p
                  className="screen-subtitle"
                  data-testid="FBP-p-scan-instruction"
                >
                  {S.FRONT_BACK_PHOTO.SUBTITLE}
                </p>

                <div
                  className="capture-grid"
                  data-testid="FBP-div-capture-grid"
                >
                  {/* Front Side Capture */}
                  {captureStep === "front" && (
                    <div
                      className="capture-box"
                      data-testid="FBP-div-capture-box-front"
                    >
                      <span className="capture-side-label">{S.FRONT_BACK_PHOTO.LABEL_FRONT_SIDE}</span>
                      <div
                        className="camera-container"
                        data-testid="FBP-div-camera-container-front"
                      >
                        {!frontIDPicture && showFrontModal && cameraError !== "busy" && (
                          <Webcam
                            key={frontWebcamKey}
                            ref={frontWebcamRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            screenshotQuality={1}
                            videoConstraints={videoConstraints}
                            onUserMedia={onFrontCameraLive}
                            onUserMediaError={handleInitError}
                            imageSmoothing={true}
                            data-testid="FBP-webcam-front"
                          />
                        )}
                        {/* EN-4743. Over the live camera only what the visitor lines up with: the
                            passport guide, the MRZ band (a line, no text) and the countdown. The
                            instructions are below the preview, so nothing covers the scan area. */}
                        {scanPassport && !frontIDPicture && showFrontModal && cameraError !== "busy" && (
                          <div className="passport-scan-overlay" data-testid="FBP-div-passport-scan-overlay">
                            <div
                              className="passport-guide"
                              ref={setPassportGuideEl}
                              data-state={passportScan.isGlare ? "glare" : passportScan.mrzData ? "read" : ""}
                            >
                              <div className="passport-mrz-band" />
                            </div>
                            {passportScan.countdownRunning && (
                              <span className="passport-scan-countdown" aria-hidden="true" data-testid="FBP-span-scan-countdown">
                                {passportScan.secondsLeft}
                              </span>
                            )}
                          </div>
                        )}
                        {/* EN-4743. Licence / state ID / Others (and ID capture only): the auto-capture countdown, in the corner. */}
                        {!scanPassport && frontAuto.running && !frontIDPicture && showFrontModal && cameraError !== "busy" && (
                          <div className="passport-scan-overlay" data-testid="FBP-div-front-auto-capture">
                            <span className="passport-scan-countdown" aria-hidden="true" data-testid="FBP-span-front-countdown">
                              {frontAuto.secondsLeft}
                            </span>
                          </div>
                        )}
                        {!frontIDPicture && showFrontModal && cameraError === "busy" && (
                          <div className="camera-permission-placeholder" data-testid="FBP-div-camera-placeholder-front-busy">
                            <i className="bi bi-camera-video-off" />
                          </div>
                        )}
                        {!frontIDPicture && !showFrontModal && (
                          <div className="camera-permission-placeholder" data-testid="FBP-div-camera-placeholder-front">
                            <i className={permissionState === "granted" ? "bi bi-camera-video" : "bi bi-camera-video-off"} />
                          </div>
                        )}

                        {frontIDPicture && (
                          <div
                            className="camera-image"
                            data-testid="FBP-div-camera-image-front"
                          >
                            <img
                              src={frontIDPicture}
                              alt="Front image cap"
                              data-testid="FBP-img-front"
                            />
                          </div>
                        )}
                      </div>
                      {/* EN-4743. Passport: no Retake while the photo is queued for sending or validateId is running. */}
                      {frontIDPicture && !(scanPassport && (isLoading || autoSubmitPending)) && (
                        <button
                          type="button"
                          className="btn btn-retake"
                          style={{ color: themeColors.btnColor }}
                          onClick={onFrontEnableCamera}
                          data-testid="FBP-button-retake-front"
                        >
                          <i
                            className="bi bi-arrow-repeat me-2"
                            data-testid="FBP-icon-retake-front"
                          ></i>
                          {S.COMMON.RETAKE}
                        </button>
                      )}
                      {!frontIDPicture && !showFrontModal && (
                        <>
                          <p className="mb-0 camera-permission-message" data-testid="FBP-p-front-permission-msg">
                            {permissionState === "denied"
                              ? S.FRONT_BACK_PHOTO.PERMISSION_DENIED
                              : permissionState === "unsupported"
                                ? S.FRONT_BACK_PHOTO.PERMISSION_UNSUPPORTED
                                : S.FRONT_BACK_PHOTO.PERMISSION_PROMPT}
                          </p>
                          {permissionState !== "granted" ? (
                            <button
                              type="button"
                              className="btn btn-enable-camera btn-shadow"
                              onClick={requestPermission}
                              disabled={permissionState === "unsupported" || permissionState === "checking"}
                              style={{
                                backgroundColor: themeColors.btnColor,
                                borderColor: themeColors.btnColor,
                                color: themeColors.btnTextColor,
                              }}
                              data-testid="FBP-button-enable-camera-front"
                            >
                              <i className="bi bi-camera-video me-2"></i>
                              {S.FRONT_BACK_PHOTO.BTN_ENABLE_CAMERA}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={onFrontEnableCamera}
                              data-testid="FBP-button-scan-front"
                            >
                              {S.FRONT_BACK_PHOTO.BTN_START_CAMERA}
                            </button>
                          )}
                        </>
                      )}
                      {!frontIDPicture && showFrontModal && cameraError !== "busy" && (
                        <>
                          {/* EN-4743. Passport: the scan instructions, below the preview. They replace the
                              generic "Position the front" line while the passport is scanned. */}
                          {scanPassport ? (
                            <div className="scan-instructions" aria-live="polite" data-testid="FBP-div-scan-instructions-front">
                              <p className="mb-0 scan-instruction" data-testid="FBP-p-front-side">
                                {S.FRONT_BACK_PHOTO.MRZ_BAND_HINT}
                              </p>
                              {passportScan.state === "scanning" && (
                                <p className="mb-0 scan-progress" data-testid="FBP-span-scan-attempt">
                                  {S.FRONT_BACK_PHOTO.MRZ_SCAN_ATTEMPT
                                    .replace("{current}", String(passportScan.attempt))
                                    .replace("{total}", String(MAX_SCAN_ATTEMPTS))}
                                </p>
                              )}
                              {passportScan.countdownRunning && (
                                <span className="visually-hidden">{passportScan.secondsLeft}</span>
                              )}
                              {passportScan.state === "scanning" && passportScan.isGlare && (
                                <p className="mb-0 scan-warning" data-testid="FBP-span-scan-glare">
                                  {S.FRONT_BACK_PHOTO.GLARE_WARNING}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="mb-0" data-testid="FBP-p-front-side">
                              {S.FRONT_BACK_PHOTO.INSTRUCTION_POSITION_FRONT}
                            </p>
                          )}
                          {/* EN-4743. A passport scan failed with attempts left: say so and offer Retry. */}
                          {scanPassport && passportScan.state === "awaitingRetry" && (
                            <>
                              <p className="mb-0 passport-scan-failed" data-testid="FBP-p-scan-failed-front">
                                {S.FRONT_BACK_PHOTO.MRZ_SCAN_FAILED}
                              </p>
                              <button
                                type="button"
                                className="btn btn-enable-camera btn-shadow"
                                onClick={passportScan.retry}
                                style={{
                                  backgroundColor: themeColors.btnColor,
                                  borderColor: themeColors.btnColor,
                                  color: themeColors.btnTextColor,
                                }}
                                data-testid="FBP-button-scan-retry-front"
                              >
                                <i className="bi bi-arrow-repeat me-2"></i>
                                {S.COMMON.RETRY}
                              </button>
                            </>
                          )}
                          {/* EN-4743. Shown only as a fallback: the passport OCR couldn't load (manual flow),
                              or the camera never went live. See showFrontCapture. */}
                          {showFrontCapture && (
                          <Button
                            ref={frontClickRef}
                            variant="outline-primary"
                            className="capture-button btn-shadow"
                            id="frontDone"
                            style={{ color: themeColors.btnColor }}
                            onClick={onFrontCaptureClick}
                            onMouseOver={() => {
                              if (!frontClickRef.current) return;
                              frontClickRef.current.style.backgroundColor =
                                themeColors.btnColor;
                              frontClickRef.current.style.color =
                                themeColors.btnTextColor;
                            }}
                            onMouseLeave={() => {
                              if (!frontClickRef.current) return;
                              frontClickRef.current.style.backgroundColor = "";
                              frontClickRef.current.style.color =
                                themeColors.btnColor;
                            }}
                            data-testid="FBP-button-click-front"
                          >
                            <i className="bi bi-circle"></i>
                            {S.COMMON.CAPTURE}
                          </Button>
                          )}
                        </>
                      )}
                      {!frontIDPicture && showFrontModal && cameraError === "busy" && (
                        <button
                          type="button"
                          className="btn btn-enable-camera btn-shadow"
                          onClick={() => handleTryNow("front")}
                          style={{
                            backgroundColor: themeColors.btnColor,
                            borderColor: themeColors.btnColor,
                            color: themeColors.btnTextColor,
                          }}
                          data-testid="FBP-button-try-now-front"
                        >
                          <i className="bi bi-arrow-repeat me-2"></i>
                          {S.FRONT_BACK_PHOTO.BTN_TRY_NOW}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Back Side Capture - Only for non-passport/others */}
                  {needsBackCapture && captureStep === "back" && (
                    <div
                      className="capture-box"
                      data-testid="FBP-div-capture-box-back"
                    >
                      <span className="capture-side-label">{S.FRONT_BACK_PHOTO.LABEL_BACK_SIDE}</span>
                      <div
                        className="camera-container"
                        data-testid="FBP-div-camera-container-back"
                      >
                        {!backIDPicture && showBackModal && cameraError !== "busy" && (
                          <Webcam
                            key={backWebcamKey}
                            ref={backWebcamRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            screenshotQuality={scanBarcode ? UPLOAD_JPEG_QUALITY : 1}
                            videoConstraints={scanBarcode ? BARCODE_VIDEO_CONSTRAINTS : videoConstraints}
                            onUserMedia={onBackCameraLive}
                            onUserMediaError={handleInitError}
                            imageSmoothing={true}
                            data-testid="FBP-webcam-back"
                          />
                        )}
                        {/* EN-4743. Over the live camera only the card guide and the countdown; the
                            instructions are below the preview, so nothing covers the barcode. */}
                        {/* ID capture only: the auto-capture countdown, in the corner, as on the front. */}
                        {!scanBarcode && backAuto.running && !backIDPicture && showBackModal && cameraError !== "busy" && (
                          <div className="passport-scan-overlay" data-testid="FBP-div-back-auto-capture">
                            <span className="passport-scan-countdown" aria-hidden="true" data-testid="FBP-span-back-countdown">
                              {backAuto.secondsLeft}
                            </span>
                          </div>
                        )}
                        {scanBarcode && !backIDPicture && showBackModal && cameraError !== "busy" && (
                          <div className="passport-scan-overlay" data-testid="FBP-div-barcode-scan-overlay">
                            <div
                              className="passport-guide id-card-guide"
                              ref={setCardGuideEl}
                              data-state={barcodeScan.isGlare ? "glare" : barcodeScan.barcode ? "read" : ""}
                            />
                            {barcodeScan.countdownRunning && (
                              <span className="passport-scan-countdown" aria-hidden="true" data-testid="FBP-span-scan-countdown-back">
                                {barcodeScan.secondsLeft}
                              </span>
                            )}
                          </div>
                        )}
                        {!backIDPicture && showBackModal && cameraError === "busy" && (
                          <div className="camera-permission-placeholder" data-testid="FBP-div-camera-placeholder-back-busy">
                            <i className="bi bi-camera-video-off" />
                          </div>
                        )}
                        {!backIDPicture && !showBackModal && (
                          <div className="camera-permission-placeholder" data-testid="FBP-div-camera-placeholder-back">
                            <i className={permissionState === "granted" ? "bi bi-camera-video" : "bi bi-camera-video-off"} />
                          </div>
                        )}

                        {backIDPicture && (
                          <div
                            className="camera-image"
                            data-testid="FBP-div-camera-image-back"
                          >
                            <img
                              src={backIDPicture}
                              alt="Back image cap"
                              data-testid="FBP-img-back"
                            />
                          </div>
                        )}
                      </div>
                      {/* EN-4743. Below the camera / photo: what's happening while the last-chance
                          read and the send run, so the few seconds before the result don't look like
                          a freeze. */}
                      {scanBarcode && (barcodeScan.readingPhoto || (backIDPicture && (autoSubmitPending || isLoading))) && (
                        <div className="scan-status" role="status" aria-live="polite" data-testid="FBP-div-scan-status-back">
                          <ClipLoader size={16} color={themeColors.btnColor} />
                          <span>
                            {barcodeScan.readingPhoto
                              ? S.FRONT_BACK_PHOTO.BARCODE_READING_PHOTO
                              : S.FRONT_BACK_PHOTO.ID_CHECKING}
                          </span>
                        </div>
                      )}
                      {/* EN-4743. Licence / state ID: no Retake while the photo is being read, queued for sending or validateId is running. */}
                      {backIDPicture && !(scanBarcode && (isLoading || autoSubmitPending || barcodeScan.readingPhoto)) && (
                        <button
                          type="button"
                          className="btn btn-retake"
                          style={{ color: themeColors.btnColor }}
                          onClick={onBackEnableCamera}
                          data-testid="FBP-button-retake-back"
                        >
                          <i
                            className="bi bi-arrow-repeat me-2"
                            data-testid="FBP-icon-retake-back"
                          ></i>
                          {S.COMMON.RETAKE}
                        </button>
                      )}
                      {!backIDPicture && !showBackModal && (
                        <>
                          <p className="mb-0 camera-permission-message" data-testid="FBP-p-back-permission-msg">
                            {permissionState === "denied"
                              ? S.FRONT_BACK_PHOTO.PERMISSION_DENIED
                              : permissionState === "unsupported"
                                ? S.FRONT_BACK_PHOTO.PERMISSION_UNSUPPORTED
                                : S.FRONT_BACK_PHOTO.PERMISSION_PROMPT}
                          </p>
                          {permissionState !== "granted" ? (
                            <button
                              type="button"
                              className="btn btn-enable-camera btn-shadow"
                              onClick={requestPermission}
                              disabled={permissionState === "unsupported" || permissionState === "checking"}
                              style={{
                                backgroundColor: themeColors.btnColor,
                                borderColor: themeColors.btnColor,
                                color: themeColors.btnTextColor,
                              }}
                              data-testid="FBP-button-enable-camera-back"
                            >
                              <i className="bi bi-camera-video me-2"></i>
                              {S.FRONT_BACK_PHOTO.BTN_ENABLE_CAMERA}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={onBackEnableCamera}
                              data-testid="FBP-button-scan-back"
                            >
                              {S.FRONT_BACK_PHOTO.BTN_START_CAMERA}
                            </button>
                          )}
                        </>
                      )}
                      {!backIDPicture && showBackModal && cameraError !== "busy" && (
                        <>
                          {/* EN-4743. Licence / state ID back: the scan instructions, below the preview.
                              They replace the generic "Position the back" line while the card is scanned. */}
                          {scanBarcode ? (
                            <div className="scan-instructions" aria-live="polite" data-testid="FBP-div-scan-instructions-back">
                              <p className="mb-0 scan-instruction" data-testid="FBP-p-back-side">
                                {S.FRONT_BACK_PHOTO.BARCODE_BAND_HINT}
                              </p>
                              {barcodeScan.state === "scanning" && (
                                <p className="mb-0 scan-progress" data-testid="FBP-span-scan-attempt-back">
                                  {S.FRONT_BACK_PHOTO.MRZ_SCAN_ATTEMPT
                                    .replace("{current}", String(barcodeScan.attempt))
                                    .replace("{total}", String(MAX_SCAN_ATTEMPTS))}
                                </p>
                              )}
                              {barcodeScan.countdownRunning && (
                                <span className="visually-hidden">{barcodeScan.secondsLeft}</span>
                              )}
                              {barcodeScan.state === "scanning" && barcodeScan.isGlare && (
                                <p className="mb-0 scan-warning" data-testid="FBP-span-scan-glare-back">
                                  {S.FRONT_BACK_PHOTO.GLARE_WARNING}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="mb-0" data-testid="FBP-p-back-side">
                              {S.FRONT_BACK_PHOTO.INSTRUCTION_POSITION_BACK}
                            </p>
                          )}
                          {/* EN-4743. Torch for dim lobbies, only where the phone has one. */}
                          {scanBarcode && cameraTuning?.torchAvailable && (
                            <button
                              type="button"
                              className="btn btn-retake"
                              style={{ color: themeColors.btnColor }}
                              onClick={onToggleTorch}
                              aria-pressed={torchOn}
                              data-testid="FBP-button-torch-back"
                            >
                              <i className={`bi ${torchOn ? "bi-lightbulb-fill" : "bi-lightbulb"} me-2`}></i>
                              {torchOn ? S.FRONT_BACK_PHOTO.TORCH_OFF : S.FRONT_BACK_PHOTO.TORCH_ON}
                            </button>
                          )}
                          {/* EN-4743. A barcode scan failed with attempts left: say so and offer Retry. */}
                          {scanBarcode && barcodeScan.state === "awaitingRetry" && (
                            <>
                              <p className="mb-0 passport-scan-failed" data-testid="FBP-p-scan-failed-back">
                                {S.FRONT_BACK_PHOTO.BARCODE_SCAN_FAILED}
                              </p>
                              <button
                                type="button"
                                className="btn btn-enable-camera btn-shadow"
                                onClick={barcodeScan.retry}
                                style={{
                                  backgroundColor: themeColors.btnColor,
                                  borderColor: themeColors.btnColor,
                                  color: themeColors.btnTextColor,
                                }}
                                data-testid="FBP-button-scan-retry-back"
                              >
                                <i className="bi bi-arrow-repeat me-2"></i>
                                {S.COMMON.RETRY}
                              </button>
                            </>
                          )}
                          {/* EN-4743. Licence / state ID back: scan only, Retry after a miss. Capture
                              shows only where scanning can't happen: the reader couldn't load, or the
                              camera never went live (safety net), so nobody is left stuck. */}
                          {showBackCapture && (
                          <Button
                            ref={backsideClickRef}
                            variant="outline-primary"
                            className="capture-button btn-shadow"
                            id="backDone"
                            style={{ color: themeColors.btnColor }}
                            onClick={onBackCaptureClick}
                            onMouseOver={() => {
                              if (!backsideClickRef.current) return;
                              backsideClickRef.current.style.backgroundColor =
                                themeColors.btnColor;
                              backsideClickRef.current.style.color =
                                themeColors.btnTextColor;
                            }}
                            onMouseLeave={() => {
                              if (!backsideClickRef.current) return;
                              backsideClickRef.current.style.backgroundColor = "";
                              backsideClickRef.current.style.color =
                                themeColors.btnColor;
                            }}
                            data-testid="FBP-button-click-back"
                          >
                            <i className="bi bi-circle"></i>
                            {S.COMMON.CAPTURE}
                          </Button>
                          )}
                        </>
                      )}
                      {!backIDPicture && showBackModal && cameraError === "busy" && (
                        <button
                          type="button"
                          className="btn btn-enable-camera btn-shadow"
                          onClick={() => handleTryNow("back")}
                          style={{
                            backgroundColor: themeColors.btnColor,
                            borderColor: themeColors.btnColor,
                            color: themeColors.btnTextColor,
                          }}
                          data-testid="FBP-button-try-now-back"
                        >
                          <i className="bi bi-arrow-repeat me-2"></i>
                          {S.FRONT_BACK_PHOTO.BTN_TRY_NOW}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="frontback-footer">
                  {captureStep === "front" && needsBackCapture ? (
                    <Button
                      variant="primary"
                      onClick={() => setCaptureStep("back")}
                      disabled={!frontIDPicture}
                      className="btn btn-shadow btn-min-width"
                      data-testid="FBP-button-continue-to-back"
                    >
                      {S.COMMON.CONTINUE}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleContinue}
                      disabled={
                        isLoading ||
                        // EN-4743. A scan session is about to send on its own: one call only.
                        autoSubmitPending ||
                        barcodeScan.readingPhoto ||
                        (!needsBackCapture && !frontIDPicture) ||
                        (needsBackCapture &&
                          (!frontIDPicture || !backIDPicture))
                      }
                      className="btn btn-shadow btn-min-width"
                      data-testid="FBP-button-next"
                    >
                      {isLoading ? (
                        <ClipLoader size={20} color="#fff" />
                      ) : (
                        S.COMMON.CONTINUE
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Validation Status Modal */}
        <Modal
          show={showValidationModal}
          onHide={() => setShowValidationModal(false)}
          centered
          backdrop="static"
          keyboard={false}
          className="modal fade modal-confirmation modal-identity-validation"
          data-testid="FBP-modal-validation-status"
        >
          {/* <Modal.Header closeButton className="modal-header border-0">
            <Modal.Title className="modal-title">
              Identity Validation
            </Modal.Title> 
          </Modal.Header> */}
          <Modal.Body
            className="modal-body text-center"
            data-testid="FBP-modal-body-validation"
          >
            {idValidationResponseObj.identityStatus === "Valid" && (
              <i
                className="bi bi-check-circle status-icon text-success"
                data-testid="FBP-icon-check-circle"
              ></i>
            )}
            {idValidationResponseObj.identityStatus !== "Valid" && (
              <i
                className="bi bi-exclamation-circle status-icon text-warning"
                data-testid="FBP-icon-exclamation-circle"
              ></i>
            )}
            <h4 data-testid="FBP-h4-identity-status">
              {idValidationResponseObj.identityStatus}
            </h4>
            <p data-testid="FBP-p-validation-message">
              {idValidationResponseObj.message}
            </p>
          </Modal.Body>
          <Modal.Footer
            className="modal-footer justify-content-center border-0"
            data-testid="FBP-modal-footer-validation"
          >
            {idValidationResponseObj.identityStatus !== "Valid" && (
              <Button
                variant="primary"
                onClick={retryIdValidationSteps}
                className="btn btn-danger"
                data-testid="FBP-button-retry"
              >
                {S.COMMON.RETRY}
              </Button>
            )}
            {idValidationResponseObj.identityStatus === "Valid" && (
              <Button
                variant="secondary"
                onClick={redirectToNextPage}
                className="btn btn-success"
                data-testid="FBP-button-continue"
              >
                {S.COMMON.CONTINUE}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
      <ConfirmationModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        data-testid="FBP-modal-confirmation"
      />
    </>
  );
};

export default FrontBackPicture;
